import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';

export type DSV2Theme = 'new-standard' | 'new-manga' | 'new-ukiyo' | null;

interface DSV2ContextType {
  /** The currently active V2 theme. null = V1 legacy (admin only). */
  activeV2Theme: DSV2Theme;
  /** Set V2 theme — called by experience bridge or admin override. */
  setV2Theme: (theme: DSV2Theme) => void;
  /** True when any V2 theme is active (false = V1 legacy). */
  isV2Active: boolean;
  /** Admin-only: true when admin has manually activated V1 legacy mode. */
  isV1Legacy: boolean;
  /** Admin-only: toggle V1 legacy mode. */
  setV1Legacy: (legacy: boolean) => void;
}

const DSV2Context = createContext<DSV2ContextType>({
  activeV2Theme: 'new-standard',
  setV2Theme: () => {},
  isV2Active: true,
  isV1Legacy: false,
  setV1Legacy: () => {},
});

export function useDesignSystemV2() {
  return useContext(DSV2Context);
}

function loadV2Fonts(theme: DSV2Theme) {
  const linkId = 'ds-v2-fonts';
  const existing = document.getElementById(linkId) as HTMLLinkElement | null;

  if (!theme) {
    existing?.remove();
    return;
  }

  // Plus Jakarta Sans, Fraunces, and Bangers are already loaded globally.
  let families = 'family=JetBrains+Mono:wght@400;500';
  if (theme === 'new-ukiyo') {
    families += '&family=Shippori+Mincho:wght@400;500;600;700;800';
    families += '&family=Hina+Mincho';
  }

  const href = `https://fonts.googleapis.com/css2?${families}&display=swap`;

  if (existing) {
    if (existing.href !== href) existing.href = href;
  } else {
    const link = document.createElement('link');
    link.id = linkId;
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }
}

export function DesignSystemV2Provider({ children }: { children: ReactNode }) {
  const { isAdmin } = useAuth();

  // V2 theme state — default 'new-standard' for everyone. The
  // V2ExperienceBridge in App.tsx updates this when the user equips a
  // Manga / Ukiyo-e mode from the cosmetics shop.
  const [activeV2Theme, setActiveV2Theme] = useState<DSV2Theme>('new-standard');

  // Admin-only V1 legacy toggle — persists in localStorage so the admin
  // can refresh and stay in V1 preview.
  const [isV1Legacy, setIsV1Legacy] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('ds-v1-legacy') === 'true';
  });

  // Effective theme: V1 legacy only available to admin; everyone else
  // always gets a V2 theme.
  const effectiveTheme: DSV2Theme = (isAdmin && isV1Legacy) ? null : activeV2Theme;

  // Apply / remove data-ds-v2 attribute on <html>. The default
  // attribute set in index.html means V2 Standard is already painted
  // before React mounts; this hook only reacts to changes after.
  useEffect(() => {
    const root = document.documentElement;
    if (effectiveTheme) {
      root.setAttribute('data-ds-v2', effectiveTheme);
      loadV2Fonts(effectiveTheme);
    } else {
      root.removeAttribute('data-ds-v2');
      loadV2Fonts(null);
    }
  }, [effectiveTheme]);

  const setV2Theme = useCallback((theme: DSV2Theme) => {
    // null is reserved for V1 legacy and is set via setV1Legacy;
    // setV2Theme(null) coerces to 'new-standard' to keep V2 always-on.
    setActiveV2Theme(theme || 'new-standard');
  }, []);

  const setV1LegacyPersist = useCallback((legacy: boolean) => {
    setIsV1Legacy(legacy);
    if (legacy) {
      localStorage.setItem('ds-v1-legacy', 'true');
    } else {
      localStorage.removeItem('ds-v1-legacy');
      // Clean up the old admin-only V2 preview key from before the
      // V2-as-default migration.
      localStorage.removeItem('ds-v2-theme');
    }
  }, []);

  return (
    <DSV2Context.Provider value={{
      activeV2Theme: effectiveTheme,
      setV2Theme,
      isV2Active: !!effectiveTheme,
      isV1Legacy: isAdmin ? isV1Legacy : false,
      setV1Legacy: setV1LegacyPersist,
    }}>
      {children}
    </DSV2Context.Provider>
  );
}
